import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from '@/i18n/hooks/useTranslation'
import { useChallenges } from '@/features/challenges/ChallengeProvider'
import { ChallengeSelectionStep } from '../components/ChallengeSelectionStep'
import type { Challenge } from '@/types/challenge'

export function ChallengeSelectionPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isLoading, isError, error } = useChallenges()
  const [selectedChallenge, setSelectedChallenge] = useState<Challenge | null>(null)

  const handleChallengeSelect = (challenge: Challenge) => {
    setSelectedChallenge(challenge)
    
    // Navigate to frequency step with challenge data
    navigate('/challenges/new/frequency', { 
      state: { 
        selectedChallenge: challenge,
        customTitle: '',
        step: 'frequency'
      }
    })
  }

  const handleCustomSelect = () => {
    // Navigate to frequency step for custom challenge
    navigate('/challenges/new/frequency', { 
      state: { 
        selectedChallenge: null,
        customTitle: '',
        step: 'frequency'
      }
    })
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-md"></div>
          <p className="text-text">{t('common.loading')}</p>
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <div className="text-center">
          <p className="text-error mb-md">{error?.message || t('errors.loadingFailed')}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="text-primary hover:text-primary-hover underline"
          >
            {t('common.retry')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-bg">
      <div className="max-w-2xl mx-auto px-lg py-2xl">
        <div className="rounded-xl">
          {/* Progress Indicator */}
          <div className="mb-2xl">
            <div className="flex items-center justify-center space-x-md">
              <div className="flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium bg-primary text-white dark:text-black">
                1
              </div>
              <div className="w-16 h-1 bg-border" />
              <div className="flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium bg-border text-text-muted">
                2
              </div>
              <div className="w-16 h-1 bg-border" />
              <div className="flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium bg-border text-text-muted">
                3
              </div>
            </div>
          </div>

          {/* Step Content */}
          <ChallengeSelectionStep
            selectedChallenge={selectedChallenge}
            onSelect={handleChallengeSelect}
            onNext={() => {}} // Not used in this context
            onCustomChallenge={handleCustomSelect}
          />
        </div>
      </div>
    </div>
  )
} 